import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateClientEntraSignInModel,
    CreateClientEntraSignInResponse,
    GetClientEntraSignInByIdResponse,
    GetClientEntraSignInSettingsResponse,
    GetClientEntraSignInsByClientIdResponse,
    LookupClientEntraSignInParams,
    LookupClientEntraSignInResponse,
    ProblemDetails,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns the Entra identities that may sign in as the client.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientEntraSignInsByClientIdHook = () => {
    const getClientEntraSignInsByClientId = useCustomIDSClient<GetClientEntraSignInsByClientIdResponse>();

    return useCallback(
        (clientId: string, signal?: AbortSignal) => {
            return getClientEntraSignInsByClientId({
                url: `/clients/${clientId}/entra-sign-ins`,
                method: 'GET',
                signal,
            });
        },
        [getClientEntraSignInsByClientId],
    );
};

export const getGetClientEntraSignInsByClientIdQueryKey = (clientId: string) => {
    return [`/clients/${clientId}/entra-sign-ins`] as const;
};

export const useGetClientEntraSignInsByClientIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                TError,
                TData
            >
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientEntraSignInsByClientIdQueryKey(clientId);

    const getClientEntraSignInsByClientId = useGetClientEntraSignInsByClientIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>> = ({
        signal,
    }) => getClientEntraSignInsByClientId(clientId, signal);

    return { queryKey, queryFn, enabled: !!clientId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientEntraSignInsByClientIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>
>;
export type GetClientEntraSignInsByClientIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientEntraSignInsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientEntraSignInsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInsByClientIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientEntraSignInsByClientIdQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Allows an Entra identity to sign in as the given client.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useCreateClientEntraSignInHook = () => {
    const createClientEntraSignIn = useCustomIDSClient<CreateClientEntraSignInResponse>();

    return useCallback(
        (clientId: string, createClientEntraSignInModel: CreateClientEntraSignInModel, signal?: AbortSignal) => {
            return createClientEntraSignIn({
                url: `/clients/${clientId}/entra-sign-ins`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createClientEntraSignInModel,
                signal,
            });
        },
        [createClientEntraSignIn],
    );
};

export const useCreateClientEntraSignInMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>,
        TError,
        { clientId: string; data: CreateClientEntraSignInModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>,
    TError,
    { clientId: string; data: CreateClientEntraSignInModel },
    TContext
> => {
    const mutationKey = ['createClientEntraSignIn'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createClientEntraSignIn = useCreateClientEntraSignInHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>,
        { clientId: string; data: CreateClientEntraSignInModel }
    > = (props) => {
        const { clientId, data } = props ?? {};

        return createClientEntraSignIn(clientId, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateClientEntraSignInMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>
>;
export type CreateClientEntraSignInMutationBody = CreateClientEntraSignInModel;
export type CreateClientEntraSignInMutationError = ErrorType<ProblemDetails>;

export const useCreateClientEntraSignIn = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>,
            TError,
            { clientId: string; data: CreateClientEntraSignInModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateClientEntraSignInHook>>>,
    TError,
    { clientId: string; data: CreateClientEntraSignInModel },
    TContext
> => {
    return useMutation(useCreateClientEntraSignInMutationOptions(options), queryClient);
};
/**
 * Looks up an Entra id in a trusted tenant before it is added as an Entra sign-in. Needs the permission to add Entra sign-ins to the client.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useLookupClientEntraSignInHook = () => {
    const lookupClientEntraSignIn = useCustomIDSClient<LookupClientEntraSignInResponse>();

    return useCallback(
        (clientId: string, params: LookupClientEntraSignInParams, signal?: AbortSignal) => {
            return lookupClientEntraSignIn({
                url: `/clients/${clientId}/entra-sign-ins/lookup`,
                method: 'GET',
                params,
                signal,
            });
        },
        [lookupClientEntraSignIn],
    );
};

export const getLookupClientEntraSignInQueryKey = (clientId: string, params?: LookupClientEntraSignInParams) => {
    return [`/clients/${clientId}/entra-sign-ins/lookup`, ...(params ? [params] : [])] as const;
};

export const useLookupClientEntraSignInQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    params: LookupClientEntraSignInParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getLookupClientEntraSignInQueryKey(clientId, params);

    const lookupClientEntraSignIn = useLookupClientEntraSignInHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>> = ({
        signal,
    }) => lookupClientEntraSignIn(clientId, params, signal);

    return { queryKey, queryFn, enabled: !!clientId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type LookupClientEntraSignInQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>
>;
export type LookupClientEntraSignInQueryError = ErrorType<ProblemDetails>;

export function useLookupClientEntraSignIn<
    TData = Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    params: LookupClientEntraSignInParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLookupClientEntraSignIn<
    TData = Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    params: LookupClientEntraSignInParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLookupClientEntraSignIn<
    TData = Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    params: LookupClientEntraSignInParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useLookupClientEntraSignIn<
    TData = Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    params: LookupClientEntraSignInParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useLookupClientEntraSignInHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useLookupClientEntraSignInQueryOptions(clientId, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns an Entra sign-in by its clientEntraSignInId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientEntraSignInByIdHook = () => {
    const getClientEntraSignInById = useCustomIDSClient<GetClientEntraSignInByIdResponse>();

    return useCallback(
        (clientEntraSignInId: string, signal?: AbortSignal) => {
            return getClientEntraSignInById({
                url: `/clients/entra-sign-ins/${clientEntraSignInId}`,
                method: 'GET',
                signal,
            });
        },
        [getClientEntraSignInById],
    );
};

export const getGetClientEntraSignInByIdQueryKey = (clientEntraSignInId: string) => {
    return [`/clients/entra-sign-ins/${clientEntraSignInId}`] as const;
};

export const useGetClientEntraSignInByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientEntraSignInId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientEntraSignInByIdQueryKey(clientEntraSignInId);

    const getClientEntraSignInById = useGetClientEntraSignInByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>> = ({
        signal,
    }) => getClientEntraSignInById(clientEntraSignInId, signal);

    return { queryKey, queryFn, enabled: !!clientEntraSignInId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientEntraSignInByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>
>;
export type GetClientEntraSignInByIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientEntraSignInById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientEntraSignInId: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientEntraSignInId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientEntraSignInId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientEntraSignInById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientEntraSignInId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientEntraSignInByIdQueryOptions(clientEntraSignInId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes an Entra sign-in by its clientEntraSignInId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useDeleteClientEntraSignInHook = () => {
    const deleteClientEntraSignIn = useCustomIDSClient<void>();

    return useCallback(
        (clientEntraSignInId: string, signal?: AbortSignal) => {
            return deleteClientEntraSignIn({
                url: `/clients/entra-sign-ins/${clientEntraSignInId}`,
                method: 'DELETE',
                signal,
            });
        },
        [deleteClientEntraSignIn],
    );
};

export const useDeleteClientEntraSignInMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>,
        TError,
        { clientEntraSignInId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>,
    TError,
    { clientEntraSignInId: string },
    TContext
> => {
    const mutationKey = ['deleteClientEntraSignIn'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteClientEntraSignIn = useDeleteClientEntraSignInHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>,
        { clientEntraSignInId: string }
    > = (props) => {
        const { clientEntraSignInId } = props ?? {};

        return deleteClientEntraSignIn(clientEntraSignInId);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteClientEntraSignInMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>
>;

export type DeleteClientEntraSignInMutationError = ErrorType<ProblemDetails>;

export const useDeleteClientEntraSignIn = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>,
            TError,
            { clientEntraSignInId: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientEntraSignInHook>>>,
    TError,
    { clientEntraSignInId: string },
    TContext
> => {
    return useMutation(useDeleteClientEntraSignInMutationOptions(options), queryClient);
};
/**
 * Returns the Entra tenants that Entra sign-ins may reference and the scope that workloads request their Entra token for.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientEntraSignInSettingsHook = () => {
    const getClientEntraSignInSettings = useCustomIDSClient<GetClientEntraSignInSettingsResponse>();

    return useCallback(
        (signal?: AbortSignal) => {
            return getClientEntraSignInSettings({ url: `/clients/entra-sign-ins/settings`, method: 'GET', signal });
        },
        [getClientEntraSignInSettings],
    );
};

export const getGetClientEntraSignInSettingsQueryKey = () => {
    return [`/clients/entra-sign-ins/settings`] as const;
};

export const useGetClientEntraSignInSettingsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>, TError, TData>
    >;
}) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientEntraSignInSettingsQueryKey();

    const getClientEntraSignInSettings = useGetClientEntraSignInSettingsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>> = ({
        signal,
    }) => getClientEntraSignInSettings(signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientEntraSignInSettingsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>
>;
export type GetClientEntraSignInSettingsQueryError = ErrorType<ProblemDetails>;

export function useGetClientEntraSignInSettings<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInSettings<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientEntraSignInSettings<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientEntraSignInSettings<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientEntraSignInSettingsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientEntraSignInSettingsQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
